import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import { searchKnowledge, createKnowledge, removeKnowledge } from '@/api/knowledge';
import type { Knowledge } from '@/api/types';
import type { RootStore } from './RootStore';

// Merchant-scoped list of Knowledge bases. Backend filters by the auth context's
// merchant; we still react to merchant switches so the list refreshes.

export class KnowledgeStore {
  @observable items: Knowledge[] = [];
  @observable totalCount = 0;
  @observable isLoading = false;
  @observable error: string | null = null;
  @observable search = '';

  constructor(private root: RootStore) {
    makeObservable(this);

    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        if (merchantId != null) {
          void this.fetch();
        } else {
          runInAction(() => {
            this.items = [];
            this.totalCount = 0;
          });
        }
      },
      { fireImmediately: true }
    );
  }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await searchKnowledge({ search: this.search, pageSize: 200 });
      runInAction(() => {
        this.items = result.data ?? [];
        this.totalCount = result.paging?.totalRecordCount ?? this.items.length;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load knowledge';
      });
    }
  }

  @action setSearch(v: string): void { this.search = v; }

  @action
  async create(input: { title: string; description?: string }): Promise<Knowledge> {
    const created = await createKnowledge(input);
    void this.fetch();
    return created;
  }

  @action
  async remove(id: number): Promise<void> {
    await removeKnowledge(id);
    runInAction(() => {
      this.items = this.items.filter((k) => k.id !== id);
      this.totalCount = Math.max(0, this.totalCount - 1);
    });
  }
}
